import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Typography,
} from "@mui/material";
import AddShoppingCartOutlinedIcon from "@mui/icons-material/AddShoppingCartOutlined";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import "./PurchaseAddInsList.css";
import SearchMobileAppFilter from "../../Common/SearchMobileAppFilter";
import { useHistory } from "react-router-dom";
import { useSsoCart } from "../../../../contexts/SsoCartContext";
import SsoServices from "../../../../services/SsoServices";
import {
  SSO_AVAILABLE_ADDINS_SUBSCRIPTION,
  SSO_MY_ADDINS_SUBSCRIPTION_LIST,
} from "../../../../constants/api-constants";
import {
  CartPageTypeEnum,
  entityTypeEnum,
  transactionType,
} from "../../../../constants/common";
import { toast } from "react-toastify";

const PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 400;
const SELECTED_TAB_STORAGE_KEY = "selectedMyAddInsSubTab";
const SELECTED_ADDIN_SUBSCRIPTION_STORAGE_KEY = "selectedAddInSubscriptionMap";

const FREQUENCY_TEXT = {
  1: "Monthly",
  2: "Quarterly",
  3: "Semi-Annual",
  4: "Annual",
  99: "One Time",
};

function getInitialTab() {
  const savedTab = localStorage.getItem(SELECTED_TAB_STORAGE_KEY);
  return savedTab === "my" || savedTab === "available" ? savedTab : "available";
}

function getPersistedSelectionMap() {
  try {
    const raw = localStorage.getItem(SELECTED_ADDIN_SUBSCRIPTION_STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function persistSelectedSubscription(addInId, selectedSubscriptionId) {
  if (!addInId || !selectedSubscriptionId) return;
  const existing = getPersistedSelectionMap();
  localStorage.setItem(
    SELECTED_ADDIN_SUBSCRIPTION_STORAGE_KEY,
    JSON.stringify({
      ...existing,
      [addInId]: selectedSubscriptionId,
    }),
  );
}

function isEligible(addIn) {
  if (addIn.published === false) return false;
  if (addIn.applicableToSubscriber === false) return false;
  if (addIn.subscriptionStatus === 1) return false;
  if (
    Array.isArray(addIn.availableSubscriptions) &&
    addIn.availableSubscriptions.some((s) => s.isSubscribed)
  ) {
    return false;
  }
  return true;
}

function getOwnershipChipData(addIn) {
  const isEnterpriseOwned =
    addIn.enterpriseName !== null || Number(addIn.accessSource) === 2;

  if (isEnterpriseOwned) {
    return {
      label: `Provided by ${addIn.enterpriseName || "Enterprise"}`,
      type: "info",
    };
  }

  return {
    label: "Purchased by You",
    type: "info",
  };
}

function getSubscriptionChipData(addIn) {
  if (addIn.isTrialActive) {
    return { label: "Trial Active", type: "success" };
  }

  if (addIn.isExpired) {
    return { label: "Expired", type: "gray" };
  }

  return { label: "Active", type: "success" };
}

function getConfigurationChipData(addIn) {
  const hasEnterpriseName =
    typeof addIn?.enterpriseName === "string"
      ? addIn.enterpriseName.trim().length > 0
      : addIn?.enterpriseName !== null && addIn?.enterpriseName !== undefined;

  if (hasEnterpriseName) {
    return null;
  }

  if (addIn.isConfigured) {
    return { label: "Configured", type: "success" };
  }

  if (addIn.isConfigurationRequired) {
    return { label: "Configuration Required", type: "inComplete" };
  }

  if (addIn.accessSource === 2 || !addIn.isConfigurationRequired) {
    return { label: "No configuration required for this add-in", type: "gray" };
  }

  return { label: "In Complete Configuration", type: "gray" };
}

export default function PurchaseAddInsList() {
  const history = useHistory();
  const cardsScrollRef = useRef(null);
  const { cart, addToCart, removeFromCart } = useSsoCart();
  // Use refs for concurrency guard and pagination state to avoid stale closures
  const loadingRef = useRef(false);
  const nextPageRef = useRef(1);
  const totalPagesRef = useRef(null);
  const requestVersionRef = useRef(0);

  const [records, setRecords] = useState([]);
  const [tab, setTab] = useState(getInitialTab);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  // Per-item cart action loading: { [addInId]: true }
  const [cartLoading, setCartLoading] = useState({});

  const fetchPage = useCallback(
    async (page) => {
      if (loadingRef.current && page !== 1) return;
      const requestVersion = ++requestVersionRef.current;
      loadingRef.current = true;
      setLoading(true);
      try {
        const endpoint =
          tab === "my"
            ? SSO_MY_ADDINS_SUBSCRIPTION_LIST
            : SSO_AVAILABLE_ADDINS_SUBSCRIPTION;

        const data = await SsoServices.ssoPost(endpoint, {
          pageSize: PAGE_SIZE,
          pageNumber: page,
          ignorePaging: false,
          searchText: search,
          filter: {
            costType: [],
            addInType: [],
            vendorIds: [],
            selectedColumns: [],
          },
          isSearchInAllQuery: true,
        });
        const newRecords = data?.result?.records ?? [];
        const tp = data?.result?.totalPages ?? 1;
        const cp = data?.result?.currentPage ?? page;

        // Ignore late responses from older requests (e.g. after fast typing/clearing).
        if (requestVersion !== requestVersionRef.current) {
          return;
        }

        totalPagesRef.current = tp;
        nextPageRef.current = cp + 1;
        setHasMore(cp < tp);
        setRecords((prev) =>
          page === 1 ? newRecords : [...prev, ...newRecords],
        );
      } catch {
        if (requestVersion === requestVersionRef.current) {
          setHasMore(false);
        }
      } finally {
        if (requestVersion === requestVersionRef.current) {
          loadingRef.current = false;
          setLoading(false);
        }
      }
    },
    [search, tab],
  );

  useEffect(() => {
    const normalizedValue = searchInput.trim();

    // Clearing search should apply immediately without waiting for debounce.
    if (normalizedValue === "") {
      setSearch("");
      return undefined;
    }

    const timer = setTimeout(() => {
      setSearch(normalizedValue);
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [searchInput]);

  // Reset and load page 1 when search changes
  useEffect(() => {
    nextPageRef.current = 1;
    totalPagesRef.current = null;
    setHasMore(true);
    setRecords([]);
    fetchPage(1);
  }, [fetchPage]);

  useEffect(() => {
    localStorage.setItem(SELECTED_TAB_STORAGE_KEY, tab);
  }, [tab]);

  // Auto-fill: if records don't cause a scrollbar, keep fetching next pages
  useEffect(() => {
    if (loading || !hasMore || !cardsScrollRef.current) {
      return;
    }
    const el = cardsScrollRef.current;
    if (el.scrollHeight <= el.clientHeight + 10) {
      fetchPage(nextPageRef.current);
    }
  }, [tab, records, loading, hasMore, fetchPage]);

  const handleScroll = useCallback(
    (e) => {
      const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
      if (
        scrollHeight - scrollTop - clientHeight < 100 &&
        !loadingRef.current &&
        hasMore
      ) {
        fetchPage(nextPageRef.current);
      }
    },
    [hasMore, fetchPage],
  );

  const handleCartToggle = useCallback(
    async (addIn) => {
      setCartLoading((prev) => ({ ...prev, [addIn.addInId]: true }));
      const firstDefaultSubscriptionOptions = addIn.subscriptions?.[0] || [];
      try {
        if (addIn.isAddedInCart) {
          await removeFromCart(
            { itemId: addIn.addInId },
            CartPageTypeEnum.ADDIN,
          );
          setRecords((prev) =>
            prev.map((r) =>
              r.addInId === addIn.addInId ? { ...r, isAddedInCart: false } : r,
            ),
          );
        } else {
          let selectedSubscriptionId = null;
          let payload;
          if (addIn.costType === 1) {
            selectedSubscriptionId = "free";
            payload = {
              id: addIn.addInId,
              quantity: 1,
              amount: 0,
              transactionType: transactionType.ADDIN_PURCHASE,
              currency: "USD",
              frequency: 99,
              entityType: entityTypeEnum.ADDIN,
              isRecurring: false,
              price: "$0",
              costType: 1,
              defaultSubscription: [],
            };
          } else {
            selectedSubscriptionId =
              firstDefaultSubscriptionOptions.addInSubscriptionId;
            payload = {
              id: addIn.addInId,
              quantity: 1,
              transactionType: transactionType.ADDIN_PURCHASE,
              currency: "USD",
              frequency: firstDefaultSubscriptionOptions.frequency,
              entityType: entityTypeEnum.ADDIN,
              isRecurring: firstDefaultSubscriptionOptions.isRecurring,
              price:
                firstDefaultSubscriptionOptions.actualValue ??
                firstDefaultSubscriptionOptions.baseValue ??
                0,
              costType: addIn.costType,
              defaultSubscription: addIn?.subscriptions?.[0] || [],
            };
          }
          const result = await addToCart(payload, CartPageTypeEnum.ADDIN);
          if (result.success) {
            persistSelectedSubscription(addIn.addInId, selectedSubscriptionId);
            setRecords((prev) =>
              prev.map((r) =>
                r.addInId === addIn.addInId ? { ...r, isAddedInCart: true } : r,
              ),
            );
          } else {
            toast.error(
              ` Failed to add to cart: ${result.error || "Unknown error"}`,
            );
          }
        }
      } catch {
        // ssoPost / ssoDelete handle session-expired redirects internally
      } finally {
        setCartLoading((prev) => ({ ...prev, [addIn.addInId]: false }));
      }
    },
    [addToCart, removeFromCart],
  );

  const openDetails = (addIn) => {
    history.push({
      pathname: `/purchase/addins/details/${addIn.addInId}`,
      state: { addInRow: addIn },
    });
  };

  const getPriceText = (addIn) => {
    if (addIn.costType === 1) return "Free";

    const subscriptions = addIn.subscriptions || [];
    if (subscriptions.length === 0) return "Price unavailable";

    const cartItem = (cart || []).find(
      (item) =>
        String(item?.itemId ?? item?.id ?? item?.addInId) ===
        String(addIn.addInId),
    );
    const selectedSubId =
      cartItem?.selectedSubscription?.addInSubscriptionId ??
      cartItem?.addInSubscriptionId ??
      cartItem?.subscriptionId ??
      getPersistedSelectionMap()[addIn.addInId];

    const selectedSub = selectedSubId
      ? subscriptions.find(
          (firstDefaultSubscriptionOptions) =>
            String(firstDefaultSubscriptionOptions.addInSubscriptionId) ===
            String(selectedSubId),
        )
      : null;

    const displaySub = selectedSub || subscriptions[0];
    const freqLabel = FREQUENCY_TEXT[displaySub.frequency] || "";
    const amount = displaySub.actualValue ?? displaySub.baseValue ?? 0;

    return `$${amount} – ${freqLabel}`;
  };

  const renderCartIcon = (addIn) => {
    if (cartLoading[addIn.addInId]) {
      return <CircularProgress size={20} style={{ color: "#1dcaff" }} />;
    }
    return addIn.isAddedInCart ? (
      <AddShoppingCartOutlinedIcon style={{ color: "#c41e3a" }} />
    ) : (
      <ShoppingCartOutlinedIcon style={{ color: "#1dcaff" }} />
    );
  };

  const hasMultipleSubscriptions = (addIn) =>
    Array.isArray(addIn.subscriptions) && addIn.subscriptions.length > 1;

  const showCartButton = (addIn) =>
    !hasMultipleSubscriptions(addIn) &&
    (addIn.costType === 1 ||
      (Array.isArray(addIn.subscriptions) && addIn.subscriptions.length >= 1));

  const availableList = tab === "available" ? records.filter(isEligible) : [];
  const myAddIns =
    tab === "my" ? records : records.filter((a) => !isEligible(a));

  return (
    <Box className="purchase-addins-container">
      <Box className="purchase-addins-search">
        <SearchMobileAppFilter
          className="addIns-purchase-search"
          value={searchInput}
          onSearch={(value) => setSearchInput(value)}
        />
      </Box>

      <Box className="purchase-addins-header">
        <Box className="purchase-tabs">
          <Box
            className={`purchase-tab-btn ${tab === "my" ? "active" : ""}`}
            onClick={() => setTab("my")}
          >
            My Add-ins
          </Box>
          <Box
            className={`purchase-tab-btn ${tab === "available" ? "active" : ""}`}
            onClick={() => setTab("available")}
          >
            Available Add-ins
          </Box>
        </Box>
      </Box>

      <Box className="purchase-addins-list-scroll">
        {loading && records.length === 0 && (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="100px"
          >
            <CircularProgress size={32} />
          </Box>
        )}
        {tab === "available" && !loading && availableList.length === 0 && (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="100px"
          >
            <Typography className="empty-state">
              No Add-ins available
            </Typography>
          </Box>
        )}
        {tab === "my" && !loading && myAddIns.length === 0 && (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="100px"
          >
            <Typography className="empty-state">
              No items in My Add-ins
            </Typography>
          </Box>
        )}

        {tab === "available" && availableList.length > 0 && (
          <Box
            onScroll={handleScroll}
            className="content-scroll-area purchase-addins-card-list-scroll"
            ref={cardsScrollRef}
          >
            {availableList.map((addIn) => (
              <Paper
                className="purchase-card"
                key={addIn.addInId}
                elevation={0}
                onClick={() => openDetails(addIn)}
                sx={{
                  cursor: "pointer",
                }}
              >
                <Box className="purchase-card-left">
                  <Typography className="available-purchase-title">
                    {addIn.name}
                  </Typography>
                  <Typography className="purchase-subtitle">
                    {addIn.vendorName}
                  </Typography>
                  <Typography className="purchase-price">
                    {getPriceText(addIn)}
                  </Typography>
                  {hasMultipleSubscriptions(addIn) && (
                    <Typography
                      className="see-more"
                      onClick={(e) => {
                        e.stopPropagation();
                        openDetails(addIn);
                      }}
                    >
                      See more options
                    </Typography>
                  )}
                </Box>

                <Box>
                  {showCartButton(addIn) && (
                    <IconButton
                      aria-label={
                        addIn.isAddedInCart ? "Remove from cart" : "Add to cart"
                      }
                      title={
                        addIn.isAddedInCart ? "Remove from cart" : "Add to cart"
                      }
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCartToggle(addIn);
                      }}
                      className="cart-btn"
                    >
                      {renderCartIcon(addIn)}
                    </IconButton>
                  )}
                </Box>
              </Paper>
            ))}

            {/* Inline loading spinner when fetching next page */}
            {loading && (
              <Box display="flex" justifyContent="center" py={2}>
                <CircularProgress size={24} />
              </Box>
            )}

            {/* End-of-list indicator */}
            {!hasMore && records.length > 0 && (
              <Box display="flex" justifyContent="center" py={1}>
                <Typography variant="caption" sx={{ color: "#fff" }}>
                  All add-ins loaded
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {tab === "my" && myAddIns.length > 0 && (
          <Box
            onScroll={handleScroll}
            className="content-scroll-area purchase-addins-card-list-scroll"
            ref={cardsScrollRef}
          >
            {myAddIns.map((a) => {
              const ownershipChip = getOwnershipChipData(a);
              const subscriptionChip = getSubscriptionChipData(a);
              const configurationChip = getConfigurationChipData(a);

              return (
                <Paper
                  key={a.addInId}
                  className="purchase-card"
                  elevation={0}
                  onClick={() =>
                    history.push({
                      pathname: `/purchase/my-addins/details/${a.addInId}`,
                      state: { addInRow: a },
                    })
                  }
                  sx={{
                    display: "flex",
                    justifyContent: "flex-start",
                    alignItems: "center",
                    cursor: "pointer",
                  }}
                >
                  <Box className="purchase-card-left">
                    <Box
                      display="flex"
                      flexDirection={{ xs: "column", sm: "row" }}
                      gap={0.5}
                    >
                      <Box>
                        <Typography
                          className="available-purchase-title"
                          noWrap
                          sx={{ marginBottom: "8px !important" }}
                        >
                          {a.name}
                        </Typography>
                        <Typography className="purchase-subtitle" noWrap>
                          {a.vendorName}
                        </Typography>
                      </Box>
                      <Box className="my-addins-chip-group">
                        <Box
                          display="flex"
                          flexDirection="row"
                          gap={2}
                          flexWrap="wrap"
                        >
                          <Chip
                            size="small"
                            variant="filled"
                            label={ownershipChip.label}
                            className={`my-addins-chip  font-weight-700 chip-type-${ownershipChip.type}`}
                          />
                          <Chip
                            size="small"
                            variant="filled"
                            label={subscriptionChip.label}
                            className={`my-addins-chip font-weight-700 chip-type-${subscriptionChip.type}`}
                          />
                        </Box>
                        <Box>
                          {configurationChip && (
                            <Chip
                              size="small"
                              variant="filled"
                              label={configurationChip.label}
                              className={`my-addins-chip font-weight-700 chip-type-${configurationChip.type}`}
                            />
                          )}
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                  <Box className="my-addins-meta">
                    <KeyboardArrowRightIcon className="my-addins-row-arrow" />
                  </Box>
                </Paper>
              );
            })}

            {/* Inline loading spinner when fetching next page */}
            {loading && (
              <Box display="flex" justifyContent="center" py={2}>
                <CircularProgress size={24} />
              </Box>
            )}

            {/* End-of-list indicator */}
            {!hasMore && records.length > 0 && (
              <Box display="flex" justifyContent="center" py={1}>
                <Typography variant="caption" sx={{ color: "#fff" }}>
                  All add-ins loaded
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}
